import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { PominieteWskazanie, PostepRozmowy, RecenzjaWskazanie } from "@shared/recenzjeTypy.js";
import type { MigawkaZapisu } from "../lib/autozapis.js";
import { opisyGrupWskazan, szkicDoZapisu, type KorektaSzkic } from "../lib/recenzja.js";
import { recenzjaApi } from "../lib/recenzjaApi.js";
import {
  MagazynSzkicow,
  SLOT_NOTATKI,
  kluczSlotuKorekty,
  rozlozSzkice,
  type SzkiceRozmowy,
} from "../lib/szkiceRecenzji.js";
import { zapiszBlobJakoPlik } from "../lib/zapiszPlik.js";
import { OPOZNIENIE_TEKSTU_MS, useAutozapis } from "./useAutozapis.js";
import { useUwagiRecenzji, type AkcjeUwag, type UwagiPrzegladu } from "./useUwagiRecenzji.js";

/**
 * Stan przegladu jednej rozmowy: postep, autozapis pol (werdykty, notatka, uwagi przy wypowiedziach) i akcje
 * jednorazowe (zakonczenie, Word). Uwagi przy wypowiedziach: hooks/useUwagiRecenzji.ts.
 *
 * Postep jest LICZNIKIEM Z ODPOWIEDZI SERWERA (OdpowiedzZapisu.postep). Zmiana werdyktu dodaje optymistyczny skok o 1,
 * zeby pasek ruszal sie od razu; odpowiedz serwera go nadpisuje. Zadania moga wrocic w innej kolejnosci niz wyslane,
 * wiec stosujemy odpowiedz tylko wtedy, gdy jest NOWSZA (pozniej wyslana) od ostatnio zastosowanej: stara odpowiedz nie
 * cofnie licznika.
 *
 * Szkice, ktorych serwer nie przyjal (zapis sie nie udal, a recenzent zdazyl wyjsc z rozmowy albo przeladowac strone),
 * zyja w sessionStorage (lib/szkiceRecenzji.ts). Przy wejsciu na rozmowe wracaja do pol (`wczytanySzkic`,
 * `wczytanaNotatka`) i trafiaja do kolejki jako niezapisane, wiec zapisza sie same.
 */

/** Stale akcje przegladu: referencja sie nie zmienia, wiec kontekst nie odswieza pol przy kazdej zmianie statusu. */
export interface AkcjePrzegladu extends AkcjeUwag {
  token: string;
  sesjaId: string;
  /** Zapis korekty wskazania: werdykt leci od razu (0), tekst po przerwie w pisaniu. `poZapisie` po udanym zapisie. */
  zapiszKorekte: (
    klucz: string,
    szkic: KorektaSzkic,
    opoznienieMs: number,
    poZapisie?: (zapisany: KorektaSzkic) => void,
  ) => void;
  /** Blur pola przy wskazaniu: wysyla to, co czeka na przerwe w pisaniu. */
  splusczKorekte: (klucz: string) => void;
  /** Notatka know-how do rozmowy (debounce jak tekst przy wskazaniu). */
  zapiszNotatke: (tekst: string) => void;
  splusczNotatke: () => void;
  /** Nazwa grupy werdyktu dla czytnika ekranu: rodzaj i poczatek cytatu odroznia wskazania tego samego parametru. */
  opisGrupy: (klucz: string) => string | undefined;
  /** Szkic wskazania przywrocony z magazynu (niezapisany w poprzedniej wizycie) albo undefined. Stala wartosc z wejscia. */
  wczytanySzkic: (klucz: string) => KorektaSzkic | undefined;
  /** Notatka przywrocona z magazynu albo null. Pusty napis to realny szkic (czyszczenie notatki). */
  wczytanaNotatka: () => string | null;
  /**
   * Wskazanie zapisuje przywrocony szkic bez udzialu komponentu, wiec komponent zglasza tu, co ma zrobic po udanym
   * zapisie (np. pokazac "Wersje recenzenta"). Zwraca funkcje wyrejestrowania.
   */
  nasluchujZapisu: (klucz: string, poZapisie: (zapisany: KorektaSzkic) => void) => () => void;
  /** +1 / -1 do licznika ocenionych, zanim odpowie serwer. */
  zmienOcenionych: (roznica: number) => void;
  ustawPrzejrzana: (przejrzana: boolean) => Promise<void>;
  pobierzWord: () => Promise<void>;
}

const KontekstPrzegladu = createContext<AkcjePrzegladu | null>(null);
export const DostawcaPrzegladu = KontekstPrzegladu.Provider;

export function useAkcjePrzegladu(): AkcjePrzegladu {
  const akcje = useContext(KontekstPrzegladu);
  if (!akcje) throw new Error("useAkcjePrzegladu wymaga DostawcaPrzegladu nad drzewem widoku.");
  return akcje;
}

/**
 * Stan autozapisu dla pol, ktore pokazuja go przy sobie (formularz uwagi). To OSOBNY kontekst od akcji, bo zmienia sie
 * przy kazdym zapisie: czytaja go tylko otwarte formularze, a wiersze transkryptu zostaja nietkniete.
 */
export interface StatusZapisuPrzegladu {
  migawka: MigawkaZapisu;
  ponow: () => void;
}

const KontekstStatusu = createContext<StatusZapisuPrzegladu | null>(null);
export const DostawcaStatusuZapisu = KontekstStatusu.Provider;

export function useStatusZapisu(): StatusZapisuPrzegladu {
  const status = useContext(KontekstStatusu);
  if (!status) throw new Error("useStatusZapisu wymaga DostawcaStatusuZapisu nad drzewem widoku.");
  return status;
}

const BRAK_WSKAZAN: readonly RecenzjaWskazanie[] = [];
const BRAK_UWAG: readonly PominieteWskazanie[] = [];
const BRAK_PARAMETROW: readonly string[] = [];
const kluczKorekty = kluczSlotuKorekty;
const KLUCZ_NOTATKI_ZAPISU = SLOT_NOTATKI;

export function usePrzegladRecenzji(
  token: string,
  sesjaId: string,
  liczbaWypowiedzi: number,
  poczatkowyPostep: PostepRozmowy,
  /**
   * Wskazania tej rozmowy: przywracamy tylko szkice istniejacych wskazan (reszta nie ma gdzie sie zapisac), a z ich
   * opisow powstaja unikalne nazwy grup werdyktu.
   */
  wskazania: readonly RecenzjaWskazanie[] = BRAK_WSKAZAN,
  /** Uwagi recenzenta zapisane na serwerze (RecenzjaRozmowa.pominiete): poczatkowa lista wspolna dla transkryptu i paska. */
  uwagiSerwera: readonly PominieteWskazanie[] = BRAK_UWAG,
  /** Tytuly parametrow rozmowy do listy "Czego dotyczy" w formularzu uwagi. */
  parametry: readonly string[] = BRAK_PARAMETROW,
): {
  postep: PostepRozmowy;
  migawka: MigawkaZapisu;
  ponow: () => void;
  status: StatusZapisuPrzegladu;
  akcje: AkcjePrzegladu;
  uwagi: Omit<UwagiPrzegladu, "akcje">;
} {
  const [magazyn] = useState(() => new MagazynSzkicow(token, sesjaId));
  const autozapis = useAutozapis(magazyn);
  // Czytane raz, przy pierwszym renderze: komponenty pol biora z tego poczatkowy stan, a efekt nizej odsyla to na serwer.
  const [szkice] = useState<SzkiceRozmowy>(() => rozlozSzkice(magazyn.wczytaj()));
  const nazwyGrup = useMemo(() => opisyGrupWskazan(wskazania), [wskazania]);
  const [postep, setPostep] = useState(poczatkowyPostep);
  const kolejnosc = useRef({ wyslano: 0, zastosowano: 0 });
  const sluchaczeZapisu = useRef(new Map<string, (zapisany: KorektaSzkic) => void>());
  const przywrocono = useRef(false);

  const { zaplanuj, splucz, splukajWszystko, ponow } = autozapis;

  const przyjmijPostep = useCallback(async <T extends { postep: PostepRozmowy }>(odpowiedz: Promise<T>): Promise<T> => {
    const numer = ++kolejnosc.current.wyslano;
    const wynik = await odpowiedz;
    if (numer > kolejnosc.current.zastosowano) {
      kolejnosc.current.zastosowano = numer;
      setPostep(wynik.postep);
    }
    return wynik;
  }, []);

  const { akcje: akcjeUwag, ...uwagi } = useUwagiRecenzji({
    token,
    sesjaId,
    liczbaWypowiedzi,
    serwerowe: uwagiSerwera,
    parametry,
    szkice: szkice.uwagi,
    kolejka: autozapis,
    magazyn,
    przyjmijPostep,
  });

  const akcje = useMemo<AkcjePrzegladu>(
    () => ({
      ...akcjeUwag,
      token,
      sesjaId,
      zapiszKorekte: (klucz, szkic, opoznienieMs, poZapisie) => {
        zaplanuj(
          kluczKorekty(klucz),
          szkic,
          async (s) => {
            await przyjmijPostep(recenzjaApi.zapiszWskazanie(token, sesjaId, szkicDoZapisu(klucz, s)));
            poZapisie?.(s);
          },
          opoznienieMs,
        );
      },
      splusczKorekte: (klucz) => splucz(kluczKorekty(klucz)),
      zapiszNotatke: (tekst) => {
        zaplanuj(
          KLUCZ_NOTATKI_ZAPISU,
          tekst,
          async (t) => {
            await przyjmijPostep(recenzjaApi.zapiszNotatke(token, sesjaId, t));
          },
          OPOZNIENIE_TEKSTU_MS,
        );
      },
      splusczNotatke: () => splucz(KLUCZ_NOTATKI_ZAPISU),
      opisGrupy: (klucz) => nazwyGrup.get(klucz),
      wczytanySzkic: (klucz) => szkice.korekty.get(klucz),
      wczytanaNotatka: () => szkice.notatka,
      nasluchujZapisu: (klucz, poZapisie) => {
        sluchaczeZapisu.current.set(klucz, poZapisie);
        return () => {
          if (sluchaczeZapisu.current.get(klucz) === poZapisie) sluchaczeZapisu.current.delete(klucz);
        };
      },
      zmienOcenionych: (roznica) => {
        setPostep((p) => ({ ...p, ocenionych: Math.max(0, Math.min(p.wskazan, p.ocenionych + roznica)) }));
      },
      ustawPrzejrzana: async (przejrzana) => {
        // Najpierw to, co czeka na przerwe w pisaniu: "zakonczylem" ma isc po ostatnich uwagach, nie przed nimi.
        splukajWszystko();
        await przyjmijPostep(recenzjaApi.zapiszPrzejrzana(token, sesjaId, przejrzana));
      },
      pobierzWord: async () => {
        const { blob, nazwaPliku } = await recenzjaApi.pobierzWord(token, sesjaId);
        zapiszBlobJakoPlik(blob, nazwaPliku);
      },
    }),
    [akcjeUwag, token, sesjaId, szkice, nazwyGrup, zaplanuj, splucz, splukajWszystko, przyjmijPostep],
  );

  // Przywrocone szkice leca na serwer od razu po wejsciu na rozmowe (raz, takze w trybie StrictMode).
  useEffect(() => {
    if (przywrocono.current) return;
    przywrocono.current = true;
    const znane = new Set(wskazania.map((w) => w.klucz));
    for (const [klucz, szkic] of szkice.korekty) {
      if (!znane.has(klucz)) continue;
      akcje.zapiszKorekte(klucz, szkic, 0, (zapisany) => sluchaczeZapisu.current.get(klucz)?.(zapisany));
    }
    if (szkice.notatka !== null) {
      akcje.zapiszNotatke(szkice.notatka);
      akcje.splusczNotatke();
    }
    // Efekt jednorazowy: wejscie na rozmowe. Dane wejsciowe sa stale w obrebie tej instancji widoku.
  }, []);

  const status = useMemo<StatusZapisuPrzegladu>(() => ({ migawka: autozapis.migawka, ponow }), [autozapis.migawka, ponow]);

  return { postep, migawka: autozapis.migawka, ponow, status, akcje, uwagi };
}
